import * as stylex from '@stylexjs/stylex';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useLocation } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { ResizableTableContainer, Table, TableLayout, Virtualizer } from 'react-aria-components';
import { pipe as rPipe, map as rMap, fromEntries as rFromEntries, pick as rPick } from 'remeda';
import {
  array as vArray,
  boolean as vBoolean,
  fallback as vFallback,
  nullish as vNullish,
  number as vNumber,
  object as vObject,
  string as vString,
  unknown as vUnknown,
} from 'valibot';

import { SecondaryActionsIconButton } from '@/components/secondary-actions-icon-button.component';
import { TableRowSecondaryActions } from '@/components/table-row-secondary-actions.component';
import { LOCAL_STORAGE } from '@/constants/storage';
import type { CustomerAuditLogEntityTypeEnum } from '@/libs/openapi';
import {
  Cell,
  Column,
  EmptyState,
  Heading,
  Paragraph,
  Row,
  Skeleton,
  type SkeletonRow,
  TableBody,
  TableHeader,
  TableOverlay,
  useLocalStorage,
  useMediaQuery,
} from '@/libs/serenity-ui';
import { breakpoints } from '@/libs/serenity-ui/constants.stylex';
import { auditLogQueries, type AuditLogTableRow } from '@/resources/audit-log/audit-log.api';
import { auditLogPolicies } from '@/resources/audit-log/audit-log.policies';
import { activityTableOptions } from '@/resources/audit-log/audit-log.table';
import type { ListRouteContext } from '@/routes/_main/_list';
import { Pagination } from '@/routes/_main/_list/-components/pagination.component';
import { Resource } from '@/types/resource.type';

const DEFAULT_SORTING = { id: 'createdAt', desc: true };
const DEFAULT_PAGING = { pageIndex: 0, pageSize: 30 };

export const Route = createFileRoute('/_main/_list/activity/')({
  component: RouteComponent,
  validateSearch: vObject({
    pagination: vFallback(
      vNullish(vObject({ pageIndex: vNumber(), pageSize: vNumber() }), DEFAULT_PAGING),
      DEFAULT_PAGING,
    ),
    columnFilters: vFallback(
      vNullish(vArray(vObject({ id: vString(), value: vUnknown() })), []),
      [],
    ),
    sorting: vFallback(
      vNullish(vObject({ id: vString(), desc: vBoolean() }), DEFAULT_SORTING),
      DEFAULT_SORTING,
    ),
  }),
  beforeLoad: ({ search, context }) => {
    if (context.user && !context.can.auditLog.view()) {
      throw redirect({ to: '/' });
    }

    const filters = rPipe(
      search.columnFilters,
      rMap(({ id, value }) => [ id, value ] as const),
      rFromEntries(),
    ) as {
      entityTypes?: CustomerAuditLogEntityTypeEnum[];
      organizationUserId?: string;
      createdAtGte?: string;
      createdAtLte?: string;
    };

    return {
      resource: Resource.activityLog,
      can: {
        [Resource.activityLog]: rPick(auditLogPolicies.bindContext({ user: context.user.original }), [ 'view' ]),
      },
      queryOptions: {
        list: auditLogQueries.list({
          limit: search.pagination.pageSize,
          page: search.pagination.pageIndex + 1,
          entityTypes: filters.entityTypes as unknown as CustomerAuditLogEntityTypeEnum,
          organizationUserId: filters.organizationUserId,
          createdAtGte: filters.createdAtGte,
          createdAtLte: filters.createdAtLte,
        }),
      },
    } satisfies ListRouteContext;
  },
  loader: ({ context }) => {
    context.queryClient.prefetchQuery(context.queryOptions.list);
  },
  staticData: {
    list: {
      heading: 'Activity',
      slots: {
        toolbarLeftComponent: ToolbarLeftComponent,
        toolbarActionsComponent: ToolbarActionsComponent,
        bottomBarComponent: Pagination,
      },
    },
  },
  head: () => ({
    meta: [
      {
        title: 'TrendMD | Activity',
      },
    ],
  }),
});

function RouteComponent(): ReactElement {
  const routeContext = Route.useRouteContext();
  const location = useLocation();

  const [ columnsConfig ] = useLocalStorage(
    LOCAL_STORAGE.columnsConfig(routeContext.resource).key,
    activityTableOptions.initialConfig,
  );

  const query = useQuery(routeContext.queryOptions.list);
  const dataRows: AuditLogTableRow[] = query.isLoading
    ? []
    : query.data?.data ?? [];

  const config = activityTableOptions(dataRows, columnsConfig);
  const columns = config.getColumns();

  const [ hasMeasuredData, setHasMeasuredData ] = useState(false);
  if (!hasMeasuredData && dataRows.length > 0) {
    setHasMeasuredData(true);
  }

  const rows: Array<AuditLogTableRow | SkeletonRow> = query.isLoading
    ? config.getSkeletonRows()
    : dataRows;

  return (
    <TableOverlay
      isFullScreen
      rows={rows}
      isRowHighlighted={(row) => {
        if (config.isSkeletonRow(row)) {
          return false;
        }
        return location.hash === `activity-${row.original.id}`;
      }}
      renderEmptyState={() => (!query.isLoading ?
        <EmptyState>
          <Heading>No activity yet</Heading>
          <Paragraph>Actions taken by your team members will appear here</Paragraph>
        </EmptyState> : null)}
      renderRowActions={(row) => {
        if (config.isSkeletonRow(row)) {
          return null;
        }
        return (
          <TableRowSecondaryActions
            hash={`activity-${row.original.id}`}
            id={row.original.id.toString()}
          />
        );
      }}>
      <Virtualizer layout={TableLayout} layoutOptions={config.layoutOptions}>
        <ResizableTableContainer
          key={hasMeasuredData ? 'data' : 'loading'}
          {...stylex.props(resizableTableContainerStyles.base)}>
          <Table
            aria-label="Activity"
            selectionMode="none"
            style={{ borderSpacing: 0 }}>
            <TableHeader columns={columns}>
              {(column) => <Column {...column} isFullScreen />}
            </TableHeader>
            <TableBody<typeof rows[number]> dependencies={[ columns, query.isLoading ]}>
              {(item) => {
                const isSkeleton = config.isSkeletonRow(item);

                if (isSkeleton) {
                  return (
                    <Row columns={columns} id={item.id}>
                      {() => (
                        <Cell isFullScreen>
                          <Skeleton />
                        </Cell>
                      )}
                    </Row>
                  );
                }

                const row = item as AuditLogTableRow;
                return (
                  <Row
                    columns={columns}
                    id={row.original.id}
                    variant="primary">
                    {(column) => {
                      const cellProps = config.getCell(column.id, row);
                      return cellProps ? <Cell {...cellProps} isFullScreen /> : <Cell isFullScreen />;
                    }}
                  </Row>
                );
              }}
            </TableBody>
          </Table>
        </ResizableTableContainer>
      </Virtualizer>
    </TableOverlay>
  );
}

function ToolbarActionsComponent(): ReactElement | false {
  const isSmallScreen = useMediaQuery(breakpoints.small);
  return !isSmallScreen && <SecondaryActionsIconButton isTableActions />;
}

function ToolbarLeftComponent(): ReactElement | false {
  const isSmallScreen = useMediaQuery(breakpoints.small);

  return isSmallScreen && <SecondaryActionsIconButton isTableActions />;
}

const resizableTableContainerStyles = stylex.create({
  base: {
    height: '100%',
    overflow: 'auto',
    scrollbarWidth: 'none',
    width: '100%',
    willChange: 'transform',
  },
});
